import { Content, Header, Page, Progress } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  TextField,
  Link,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Alert from '@material-ui/lab/Alert';
import { useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { CatalogGapsPanel, CatalogGapComponent, InactiveJiraMapping } from './CatalogGapsPanel';
import { CatalogBoardSummary } from './jiraBoardTypes';

type JiraBoard = {
  id: number;
  name: string;
  type?: string;
  projectKey?: string;
  projectName?: string;
  url?: string;
};

type JiraProject = {
  key: string;
  name: string;
  lastIssueUpdateTime?: string;
};

type BoardCatalogSummary = CatalogBoardSummary;

type BoardCatalogLinkage = {
  summaries?: BoardCatalogSummary[];
  untaggedComponents?: CatalogGapComponent[];
  inactiveMappings?: InactiveJiraMapping[];
  inactiveCheckAvailable?: boolean;
};

function formatLastActivity(iso?: string): string {
  if (!iso) {
    return 'No recent activity';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }
  return date.toLocaleString();
}

function entityRefToCatalogPath(entityRef: string): string | undefined {
  const [kindPart, namePart] = entityRef.split(':');
  if (!kindPart || !namePart) {
    return undefined;
  }
  const [namespace, name] = namePart.split('/');
  if (!namespace || !name) {
    return undefined;
  }
  return `/catalog/${namespace}/${kindPart}/${name}`;
}

type CatalogLinkageState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; count: number; entityRefs: string[] };

function catalogLinkageForProject(
  projectKey: string,
  summaryByProject: Map<string, BoardCatalogSummary>,
  summaryLoading: boolean,
  summaryError: Error | undefined,
): CatalogLinkageState {
  if (summaryLoading) {
    return { status: 'loading' };
  }
  if (summaryError) {
    return { status: 'unavailable' };
  }
  const summary = summaryByProject.get(projectKey);
  return {
    status: 'ready',
    count: summary?.catalogComponentCount ?? 0,
    entityRefs: summary?.entityRefs ?? [],
  };
}

function catalogLinkageSummaryLabel(linkage: CatalogLinkageState): string {
  if (linkage.status === 'loading') {
    return 'Loading linked items…';
  }
  if (linkage.status === 'unavailable') {
    return 'Linked items unavailable';
  }
  return `${linkage.count} linked item(s)`;
}

function CatalogLinkageDetails({ linkage }: { linkage: CatalogLinkageState }) {
  if (linkage.status === 'loading') {
    return (
      <Typography variant="caption" color="textSecondary">
        {' '}Loading…
      </Typography>
    );
  }
  if (linkage.status === 'unavailable') {
    return (
      <Typography variant="caption" color="textSecondary">
        {' '}Unavailable
      </Typography>
    );
  }
  if (linkage.entityRefs.length === 0) {
    return (
      <Typography variant="caption" color="textSecondary">
        {' '}None linked yet
      </Typography>
    );
  }

  return (
    <>
      {linkage.entityRefs.map(ref => (
        <Box key={ref} style={{ lineHeight: 1.5 }}>
          <Link href={entityRefToCatalogPath(ref)} target="_blank" rel="noopener noreferrer">
            {ref}
          </Link>
        </Box>
      ))}
    </>
  );
}

export function JiraBoardsPage() {
  const [search, setSearch] = useState('');
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const overviewState = useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('jira');
    const [boardsResponse, projectsResponse] = await Promise.all([
      fetchApi.fetch(`${baseUrl}/boards/active`, { credentials: 'include' }),
      fetchApi.fetch(`${baseUrl}/projects`, { credentials: 'include' }),
    ]);
    if (!boardsResponse.ok) {
      throw new Error(`Jira active boards request failed with ${boardsResponse.status}`);
    }
    if (!projectsResponse.ok) {
      throw new Error(`Jira projects request failed with ${projectsResponse.status}`);
    }
    const boardsPayload = (await boardsResponse.json()) as { boards?: JiraBoard[] };
    const projectsPayload = (await projectsResponse.json()) as { projects?: JiraProject[] };
    return {
      boards: boardsPayload.boards ?? [],
      projects: projectsPayload.projects ?? [],
    };
  }, [discoveryApi, fetchApi]);

  const summaryState = useAsync(async () => {
    if (overviewState.loading) {
      return undefined;
    }

    const baseUrl = await discoveryApi.getBaseUrl('jira');
    const projectKeys =
      overviewState.error || !overviewState.value
        ? undefined
        : overviewState.value.projects.map(project => project.key);
    const query =
      projectKeys !== undefined
        ? `?activeProjectKeys=${encodeURIComponent(projectKeys.join(','))}`
        : '';
    const response = await fetchApi.fetch(`${baseUrl}/board-catalog-summary${query}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Jira catalog summary request failed with ${response.status}`);
    }
    const payload = (await response.json()) as BoardCatalogLinkage;
    return {
      summaries: (payload.summaries ?? []).map(summary => ({
        ...summary,
        updatableEntityRefs: summary.updatableEntityRefs ?? [],
        inheritedEntityRefs: summary.inheritedEntityRefs ?? [],
      })),
      untaggedComponents: payload.untaggedComponents ?? [],
      inactiveMappings: payload.inactiveMappings ?? [],
      inactiveCheckAvailable: payload.inactiveCheckAvailable ?? false,
    };
  }, [discoveryApi, fetchApi, overviewState.loading, overviewState.error, overviewState.value?.projects]);

  const summaryByProject = useMemo(
    () => new Map((summaryState.value?.summaries ?? []).map(summary => [summary.projectKey, summary] as const)),
    [summaryState.value?.summaries],
  );

  const searchTerm = search.trim().toLowerCase();

  const filteredUntaggedComponents = useMemo(() => {
    const components = summaryState.value?.untaggedComponents ?? [];
    if (!searchTerm) {
      return components;
    }
    return components.filter(component =>
      component.entityRef.toLowerCase().includes(searchTerm),
    );
  }, [searchTerm, summaryState.value?.untaggedComponents]);

  const filteredInactiveMappings = useMemo(() => {
    const mappings = summaryState.value?.inactiveMappings ?? [];
    if (!searchTerm) {
      return mappings;
    }
    return mappings.filter(
      item =>
        item.entityRef.toLowerCase().includes(searchTerm) ||
        item.projectKeys.some(key => key.toLowerCase().includes(searchTerm)),
    );
  }, [searchTerm, summaryState.value?.inactiveMappings]);

  const gapsLoading = overviewState.loading || summaryState.loading;

  const inactiveCheckUnavailable =
    !gapsLoading &&
    !summaryState.error &&
    summaryState.value?.inactiveCheckAvailable === false;

  const jiraProjectsState = useAsync(async () => {
    if (overviewState.loading) {
      return undefined;
    }
    if (!overviewState.error && overviewState.value?.projects) {
      return overviewState.value.projects;
    }

    const baseUrl = await discoveryApi.getBaseUrl('jira');
    const response = await fetchApi.fetch(`${baseUrl}/projects`, { credentials: 'include' });
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as { projects?: JiraProject[] };
    return payload.projects ?? [];
  }, [
    discoveryApi,
    fetchApi,
    overviewState.error,
    overviewState.loading,
    overviewState.value?.projects,
  ]);

  const catalogSummaries = useMemo(
    () => summaryState.value?.summaries ?? [],
    [summaryState.value?.summaries],
  );

  const jiraProjectOptions = useMemo(
    () =>
      (jiraProjectsState.value ?? []).map(project => ({
        key: project.key,
        name: project.name,
      })),
    [jiraProjectsState.value],
  );

  const activityByProject = useMemo(
    () =>
      new Map(
        (overviewState.value?.projects ?? []).map(project => [project.key, project.lastIssueUpdateTime] as const),
      ),
    [overviewState.value?.projects],
  );

  const projects = useMemo(() => {
    const grouped = new Map<
      string,
      { projectName: string; boards: JiraBoard[]; lastIssueUpdateTime?: string }
    >();
    for (const board of overviewState.value?.boards ?? []) {
      const projectKey = board.projectKey ?? 'UNKNOWN';
      const entry = grouped.get(projectKey) ?? {
        projectName: board.projectName ?? 'Unknown project',
        boards: [],
        lastIssueUpdateTime: activityByProject.get(projectKey),
      };
      entry.boards.push(board);
      grouped.set(projectKey, entry);
    }

    return Array.from(grouped.entries())
      .map(([projectKey, value]) => ({
        projectKey,
        projectName: value.projectName,
        lastIssueUpdateTime: value.lastIssueUpdateTime ?? activityByProject.get(projectKey),
        boards: value.boards.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => {
        const aTime = a.lastIssueUpdateTime ? Date.parse(a.lastIssueUpdateTime) : 0;
        const bTime = b.lastIssueUpdateTime ? Date.parse(b.lastIssueUpdateTime) : 0;
        if (bTime !== aTime) {
          return bTime - aTime;
        }
        return a.projectKey.localeCompare(b.projectKey);
      });
  }, [activityByProject, overviewState.value?.boards]);

  const filteredProjects = useMemo(() => {
    if (!searchTerm) {
      return projects;
    }
    return projects
      .map(project => ({
        ...project,
        boards: project.boards.filter(
          board =>
            board.name.toLowerCase().includes(searchTerm) ||
            (board.type ?? '').toLowerCase().includes(searchTerm),
        ),
      }))
      .filter(
        project =>
          project.projectKey.toLowerCase().includes(searchTerm) ||
          project.projectName.toLowerCase().includes(searchTerm) ||
          project.boards.length > 0,
      );
  }, [projects, searchTerm]);

  return (
    <Page themeId="tool">
      <Header title="Jira Boards" subtitle="Active Jira projects and boards" />
      <Content>
        <Box display="flex" justifyContent="flex-end" width="100%" style={{ marginBottom: 12 }}>
          <Box width="100%" maxWidth={420} minWidth={0}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Search"
              placeholder="Projects, boards, or components"
              value={search}
              onChange={event => setSearch(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        <CatalogGapsPanel
          untaggedComponents={filteredUntaggedComponents}
          inactiveMappings={filteredInactiveMappings}
          catalogInactiveMappings={summaryState.value?.inactiveMappings ?? []}
          loading={gapsLoading}
          error={summaryState.error}
          searchActive={Boolean(searchTerm)}
          inactiveCheckUnavailable={inactiveCheckUnavailable}
          jiraProjects={jiraProjectOptions}
          catalogSummaries={catalogSummaries}
        />

        {overviewState.loading ? <Progress /> : null}
        {overviewState.error ? <Alert severity="error">{overviewState.error.message}</Alert> : null}

        {!overviewState.loading && !overviewState.error ? (
          <>
            <Box style={{ margin: '24px 0 16px' }}>
              <Divider />
              <Box style={{ marginTop: 16 }}>
                <Typography variant="h6">Active Jira projects</Typography>
                <Typography variant="body2" color="textSecondary">
                  {filteredProjects.length} project(s),{' '}
                  {filteredProjects.reduce((total, p) => total + p.boards.length, 0)} board(s)
                </Typography>
              </Box>
            </Box>

            {filteredProjects.map(project => {
              const catalogLinkage = catalogLinkageForProject(
                project.projectKey,
                summaryByProject,
                summaryState.loading,
                summaryState.error,
              );

              return (
              <Accordion key={project.projectKey} TransitionProps={{ unmountOnExit: true, timeout: 120 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ minHeight: 48 }}>
                  <Box display="flex" flexWrap="wrap" alignItems="center" width="100%" gridGap={12}>
                    <Typography variant="subtitle1">
                      {project.projectKey} - {project.projectName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Last activity: {formatLastActivity(project.lastIssueUpdateTime)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {project.boards.length} board(s)
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {catalogLinkageSummaryLabel(catalogLinkage)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails style={{ display: 'block', paddingTop: 0 }}>
                  {project.boards.map(board => (
                    <Box
                      key={board.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      style={{
                        padding: '6px 0',
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                      }}
                    >
                      {board.url ? (
                        <Link href={board.url} target="_blank" rel="noopener noreferrer">
                          {board.name}
                        </Link>
                      ) : (
                        <Typography variant="body2">{board.name}</Typography>
                      )}
                      <Typography variant="caption" color="textSecondary">
                        {board.type ?? 'unknown'}
                      </Typography>
                    </Box>
                  ))}
                  <Box style={{ marginTop: 8 }}>
                    <Typography variant="caption" color="textSecondary">
                      Linked catalog items
                    </Typography>
                    <CatalogLinkageDetails linkage={catalogLinkage} />
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
            })}
          </>
        ) : null}
      </Content>
    </Page>
  );
}
